import type { GetServerSidePropsContext } from 'next'
import { isNil } from 'ramda'

import type {
  Article,
  CategoryListResponseDataItemCustom,
  GlobalResponse,
  NavigationResponse,
  SharedSeoComponent,
} from '@twisto/api-cms'

import { Seo } from '../../components/atoms'
import { Navigation } from '../../components/navigation'
import { getArticle, getCategory, getNavigation } from '../../utils/api'
import { ArticleView } from '../../views'

type ServerSideProps = {
  category: CategoryListResponseDataItemCustom['attributes']
  navigation?: NavigationResponse
  seo?: SharedSeoComponent
  article?: Article
  pageContext: { path?: string | string[] }
}

export type DynamicPageProps = ServerSideProps & {
  global: GlobalResponse | null
}

const DynamicPage = ({
  article,
  category,
  seo,
  global,
  navigation,
}: DynamicPageProps) => {
  const metadataWithDefaults = {
    ...global?.data?.seo,
    ...seo,
  }
  const navigationData = navigation?.data

  if (isNil(article)) {
    return {
      notFound: true,
    }
  }

  let structuredData

  if (!isNil(article.shortAnswer)) {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: article.headline,
          acceptedAnswer: {
            '@type': 'Answer',
            text: article.shortAnswer,
          },
        },
      ],
    }
  }

  return (
    <>
      <Seo {...metadataWithDefaults} structuredData={structuredData} />
      {!isNil(navigationData) && <Navigation {...navigationData} />}

      <ArticleView article={article} category={category} />
    </>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { params } = context

  const url = [params?.path ?? ''].flat().join('/')
  const articleData = await getArticle(`/article/${url}`)

  if (isNil(articleData.data)) {
    return {
      notFound: true,
    }
  }

  const categoryData = await getCategory(articleData.data.category?.path ?? '')

  if (isNil(categoryData.data)) {
    return {
      notFound: true,
    }
  }

  const navigationId = categoryData.data.navigation?.id ?? null
  const navigation = !isNil(navigationId)
    ? await getNavigation(navigationId)
    : undefined

  return {
    props: {
      navigation: navigation ?? null,
      category: categoryData.data,
      article: articleData.data,
      pageContext: {
        path: articleData.data.path,
      },
    },
  }
}

export default DynamicPage
